import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem,
  FormControl, InputLabel, Select, Alert, CircularProgress, Chip,
  Grid, Divider, Paper, IconButton, Tooltip, LinearProgress
} from '@mui/material';
import {
  CloudUpload, CheckCircle, Cancel, AccessTime, Info, Badge,
  CreditCard, HowToVote, Description, Refresh, Warning, VerifiedUser
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { kycAPI } from 'services/api';

const KYCSubmission = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    kyc_type: 'individual',
    document_type: 'aadhaar',
    document_number: '',
    document_front_url: '',
    document_back_url: '',
    selfie_url: '',
    document_expiry_date: ''
  });

  const documentTypes = [
    { value: 'aadhaar', label: 'Aadhaar Card', icon: <CreditCard />, requiresBack: true },
    { value: 'pan', label: 'PAN Card', icon: <Badge />, requiresBack: false },
    { value: 'voter_id', label: 'Voter ID', icon: <HowToVote />, requiresBack: true },
    { value: 'passport', label: 'Passport', icon: <Description />, requiresBack: true, hasExpiry: true },
    { value: 'driving_license', label: 'Driving License', icon: <Description />, requiresBack: true, hasExpiry: true }
  ];

  useEffect(() => {
    loadKYCData();
  }, []);

  const loadKYCData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statusRes, submissionsRes] = await Promise.all([
        kycAPI.getStatus(),
        kycAPI.getMySubmissions()
      ]);
      
      setKycStatus(statusRes.data);
      setSubmissions(submissionsRes.data || []);
    } catch (err) {
      console.error('Error loading KYC data:', err);
      setError('Failed to load KYC information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Basic validation
      if (!formData.document_number.trim()) {
        throw new Error('Document number is required');
      }
      if (!formData.document_front_url.trim()) {
        throw new Error('Front image of document is required');
      }

      const selectedDocType = documentTypes.find(d => d.value === formData.document_type);
      if (selectedDocType?.requiresBack && !formData.document_back_url.trim()) {
        throw new Error('Back image of document is required');
      }

      await kycAPI.submit({
        kyc_type: formData.kyc_type,
        document_type: formData.document_type,
        document_number: formData.document_number.trim(),
        document_front_url: formData.document_front_url.trim(),
        document_back_url: formData.document_back_url.trim() || null,
        selfie_url: formData.selfie_url.trim() || null,
        document_expiry_date: formData.document_expiry_date || null
      });

      setSuccess('KYC submitted successfully! Our team will review your documents.');
      await loadKYCData();
      
      // Reset form
      setFormData({
        kyc_type: 'individual',
        document_type: 'aadhaar',
        document_number: '',
        document_front_url: '',
        document_back_url: '',
        selfie_url: '',
        document_expiry_date: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to submit KYC');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <AccessTime />, label: 'Pending Review' },
      under_review: { color: 'info', icon: <Info />, label: 'Under Review' },
      approved: { color: 'success', icon: <CheckCircle />, label: 'Approved' },
      rejected: { color: 'error', icon: <Cancel />, label: 'Rejected' },
      expired: { color: 'default', icon: <Warning />, label: 'Expired' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip 
        icon={config.icon}
        label={config.label}
        color={config.color}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canSubmit = !kycStatus?.kyc_submitted || kycStatus?.kyc_status === 'rejected';
  const selectedDocType = documentTypes.find(d => d.value === formData.document_type);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            KYC Verification
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete your KYC to create insurance policies
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadKYCData} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* KYC Status Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            background: kycStatus?.can_create_policy 
              ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
              : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '1px solid',
            borderColor: kycStatus?.can_create_policy ? 'success.light' : 'warning.light'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VerifiedUser sx={{ 
                  fontSize: 40, 
                  color: kycStatus?.can_create_policy ? 'success.main' : 'warning.main',
                  mr: 2 
                }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    KYC Status
                  </Typography>
                  {getStatusChip(kycStatus?.kyc_status || 'not_submitted')}
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {kycStatus?.message}
              </Typography>
              
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'background.paper', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="caption" color="text.secondary">
                  Policy Creation Status
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 600, 
                  color: kycStatus?.can_create_policy ? 'success.main' : 'warning.main' 
                }}>
                  {kycStatus?.can_create_policy ? '✓ Allowed' : '✗ Blocked'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Submission Form */}
        <Grid item xs={12} md={8}>
          {canSubmit ? (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  {kycStatus?.kyc_status === 'rejected' ? 'Resubmit KYC Documents' : 'Submit KYC Documents'}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {/* KYC Type */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>KYC Type</InputLabel>
                        <Select
                          value={formData.kyc_type}
                          label="KYC Type"
                          onChange={(e) => setFormData({ ...formData, kyc_type: e.target.value })}
                        >
                          <MenuItem value="individual">Individual</MenuItem>
                          <MenuItem value="company">Company</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Document Type */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Document Type</InputLabel>
                        <Select
                          value={formData.document_type}
                          label="Document Type"
                          onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                        >
                          {documentTypes.map((doc) => (
                            <MenuItem key={doc.value} value={doc.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {doc.icon}
                                {doc.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Document Number */}
                    <Grid item xs={12} sm={selectedDocType?.hasExpiry ? 6 : 12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Document Number"
                        placeholder={formData.document_type === 'aadhaar' ? 'XXXX XXXX XXXX' : 'Enter document number'}
                        value={formData.document_number}
                        onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                        required
                        helperText={
                          formData.document_type === 'aadhaar' ? '12-digit Aadhaar number' :
                          formData.document_type === 'pan' ? '10-character PAN' : ''
                        }
                      />
                    </Grid>

                    {/* Document Expiry (for passport, driving license) */}
                    {selectedDocType?.hasExpiry && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label="Document Expiry Date"
                          value={formData.document_expiry_date}
                          onChange={(e) => setFormData({ ...formData, document_expiry_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        Document Images (Provide URLs to uploaded images)
                      </Typography>
                    </Grid>

                    {/* Front Image URL */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Front Image URL *"
                        placeholder="https://storage.example.com/doc-front.jpg"
                        value={formData.document_front_url}
                        onChange={(e) => setFormData({ ...formData, document_front_url: e.target.value })}
                        required
                        InputProps={{
                          startAdornment: <CloudUpload sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    {/* Back Image URL */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Back Image URL ${selectedDocType?.requiresBack ? '*' : '(Optional)'}`}
                        placeholder="https://storage.example.com/doc-back.jpg"
                        value={formData.document_back_url}
                        onChange={(e) => setFormData({ ...formData, document_back_url: e.target.value })}
                        required={selectedDocType?.requiresBack}
                        InputProps={{
                          startAdornment: <CloudUpload sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    {/* Selfie URL */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Selfie with Document URL (Optional)"
                        placeholder="https://storage.example.com/selfie.jpg"
                        value={formData.selfie_url}
                        onChange={(e) => setFormData({ ...formData, selfie_url: e.target.value })}
                        helperText="A selfie holding your ID document for additional verification"
                        InputProps={{
                          startAdornment: <CloudUpload sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={submitting}
                          startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
                          sx={{ minWidth: 180 }}
                        >
                          {submitting ? 'Submitting...' : 'Submit KYC'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  KYC Already Submitted
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {kycStatus?.message}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Previous Submissions */}
        {submissions.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Submission History
                </Typography>
                
                <Grid container spacing={2}>
                  {submissions.map((submission) => (
                    <Grid item xs={12} sm={6} md={4} key={submission.id}>
                      <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                            {submission.document_type.replace('_', ' ')}
                          </Typography>
                          {getStatusChip(submission.verification_status)}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Document: {submission.document_number_masked}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary">
                          Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                        </Typography>
                        
                        {submission.rejection_reason && (
                          <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                            <Typography variant="caption">
                              {submission.rejection_reason}
                            </Typography>
                          </Alert>
                        )}
                        
                        {submission.verified_at && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            Verified: {new Date(submission.verified_at).toLocaleDateString()}
                            {submission.verifier_name && ` by ${submission.verifier_name}`}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Info Note */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> KYC verification is mandatory for creating insurance policies. 
          Our team will review your documents within 24-48 hours. You will be notified once your KYC is verified.
        </Typography>
      </Alert>
    </Box>
  );
};

export default KYCSubmission;

